// ./src/pages/private/DisplayThemesPage/DisplayThemesPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { useThemes } from "@/hooks";
import { Theme } from "@/utils/api";

import { DashboardLayout } from "@/components/layouts/DashboardLayout";

import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { ThemesTable, ThemeFormModal } from "@/components/features";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const DisplayThemesPage: React.FC<PageProps> = (props: PageProps) => {
  // Local search state
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Use the themes hook for all data management
  const {
    // Data state
    themes,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingThemeId,
    selectedTheme,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedTheme,

    // Error handling
    lastError,
    clearError,

    // Helper operations
    //reset,
  } = useThemes({
    autoLoad: false, // We'll manually load themes on mount
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [themeToDelete, setThemeToDelete] = useState<Theme | null>(null);

  // Effect to load themes on mount
  useEffect(() => {
    // Load themes on component mount
    list({}, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
  }, []);

  // Handle search with debouncing
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);
      // Apply search filter
      const newFilters = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, setFilters, list, sortBy]
  );

  // Handle add theme button click
  const handleAddTheme = useCallback(() => {
    setSelectedTheme(null);
    setFormModalOpened(true);
  }, [setSelectedTheme]);

  // Handle edit theme
  const handleEditTheme = useCallback(
    (theme: Theme) => {
      setSelectedTheme(theme);
      setFormModalOpened(true);
    },
    [setSelectedTheme]
  );

  const handleDuplicateTheme = useCallback(
    (theme: Theme) => {
      setSelectedTheme({ ...theme, id: "" });
      setFormModalOpened(true);
    },
    [setSelectedTheme]
  );

  // Handle view theme (could navigate to theme preview page)
  // const handleViewTheme = useCallback((theme: Theme) => {
  //   // TODO: Navigate to theme preview page or show preview modal
  //   console.log("View theme:", theme);
  // }, []);

  // Handle remove theme button click
  const handleRemoveThemeClick = useCallback((theme: Theme) => {
    setThemeToDelete(theme);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (themeToDelete?.id) {
      const success = await remove(themeToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setThemeToDelete(null);
      }
      // Error handling is managed by the hook
    }
  }, [themeToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setThemeToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedTheme(null);
    clearError();
  }, [setSelectedTheme, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle theme creation
  const handleCreateTheme = useCallback(
    async (themeData: any) => {
      const result = await create(themeData);
      if (result) {
        setFormModalOpened(false);
        setSelectedTheme(null);
      }
      return result;
    },
    [create, setSelectedTheme]
  );

  // Handle theme update
  const handleUpdateTheme = useCallback(
    async (themeId: string, themeData: any) => {
      const result = await update(themeId, themeData);
      if (result) {
        setFormModalOpened(false);
        setSelectedTheme(null);
      }
      return result;
    },
    [update, setSelectedTheme]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-theme"
                  iconName="Plus"
                  onClick={handleAddTheme}
                  disabled={isCreating || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search themes..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Themes table */}
        <ThemesTable
          themes={themes}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditTheme={handleEditTheme}
          onRemoveTheme={handleRemoveThemeClick}
          onDuplicateTheme={handleDuplicateTheme}
          //onViewTheme={handleViewTheme}
          removingThemeId={removingThemeId}
          size="md"
        />

        {/* Empty state when no themes */}
        <EmptyState
          display={isEmpty && !loading}
          title="No themes yet"
          description="Start by creating your first display theme"
          actionIcon="Plus"
          actionLabel="Add Theme"
          onAction={handleAddTheme}
        />
      </Stack>

      {/* Theme form modal for create/edit */}
      <ThemeFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        theme={selectedTheme}
        onCreateTheme={handleCreateTheme}
        onUpdateTheme={handleUpdateTheme}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Theme"
        message={`Are you sure you want to delete "${themeToDelete?.name}"?`}
        details="This action cannot be undone. The theme configuration will be permanently removed."
        confirmInProgress={removingThemeId === themeToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
